/**
 * useHoverEffect Hook - centralized hover animation
 *
 * Consolidates hover handlers used by multiple components for DRYness.
 * Returns onMouseEnter and onMouseLeave handlers applying transform and
 * optional shadow effects.
 */
import { useCallback } from 'react'; // get hook utilities from react
import type { MouseEvent } from 'react';

/**
 * Delivers reusable hover handlers so feature components share consistent animation behaviour without duplicating logic.
 * @param transformIn - CSS transform applied on hover entry to communicate affordance.
 * @param transformOut - CSS transform restored on hover exit to reset layout.
 * @param shadowIn - Optional box shadow applied on enter for accent emphasis.
 * @param shadowOut - Optional box shadow restored on leave to remove accent.
 * @returns Handlers object containing memoized enter/leave callbacks for UI binding.
 */
function useHoverEffect(transformIn: string, transformOut: string, shadowIn = '', shadowOut = '') { // create hover handler pair
  console.log(`useHoverEffect is running with ${transformIn}`); // entry log for debugging
  const onMouseEnter = useCallback((e: MouseEvent<HTMLElement>) => { // handler applying hover styles
    console.log(`onMouseEnter is running`); // log when hover starts
    e.currentTarget.style.transform = transformIn; // apply transform for hover
    if (shadowIn) e.currentTarget.style.boxShadow = shadowIn; // apply shadow when provided
    console.log(`onMouseEnter has run resulting in ${e.currentTarget.style.transform}`); // confirm end state
  }, [transformIn, shadowIn]);

  const onMouseLeave = useCallback((e: MouseEvent<HTMLElement>) => { // handler resetting hover styles
    console.log(`onMouseLeave is running`); // log when hover ends
    e.currentTarget.style.transform = transformOut; // reset transform when leaving
    if (shadowOut) e.currentTarget.style.boxShadow = shadowOut; // reset shadow when provided
    console.log(`onMouseLeave has run resulting in ${e.currentTarget.style.transform}`); // confirm final state
  }, [transformOut, shadowOut]);

  const handlers = { onMouseEnter, onMouseLeave }; // object for return
  console.log(`useHoverEffect is returning ${JSON.stringify(Object.keys(handlers))}`); // exit log
  return handlers; // expose hover handlers
}

export { useHoverEffect }; // expose named export so generated tests and hook consumers can access the symbol explicitly
export default useHoverEffect; // export hook for components
